// src/features/teacher/teacherSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_URL } from "../../components/01_config/api";

// const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

// List of students for this teacher
export const fetchTeacherStudents = createAsyncThunk(
  "teacher/fetchStudents",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.auth.token;

    if (!token) {
      return thunkAPI.rejectWithValue("Not authenticated");
    }

    try {
      const res = await fetch(`${API_URL}/teacher/students`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = body.error || `Request failed with ${res.status}`;
        return thunkAPI.rejectWithValue(msg);
      }

      return body; // array of students
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || "Network error");
    }
  }
);

// Lessons for a specific student
export const fetchTeacherStudentLessons = createAsyncThunk(
  "teacher/fetchStudentLessons",
  async (studentId, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.auth.token;

    if (!token) {
      return thunkAPI.rejectWithValue("Not authenticated");
    }

    try {
      const res = await fetch(
        `${API_URL}/teacher/students/${studentId}/lessons`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = body.error || `Request failed with ${res.status}`;
        return thunkAPI.rejectWithValue(msg);
      }

      // body = { student, lessons: [...] }
      return body;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || "Network error");
    }
  }
);

// Unlock a specific lesson for the student
export const unlockStudentLesson = createAsyncThunk(
  "teacher/unlockStudentLesson",
  async ({ studentId, lessonOrder }, thunkAPI) => {
    const state = thunkAPI.getState();
    const token = state.auth.token;
    const lessons = state.teacher.studentLessons || [];

    if (!token) {
      return thunkAPI.rejectWithValue("Not authenticated");
    }

    // Find lesson by order
    const lesson = lessons.find((l) => l.order === lessonOrder);
    if (!lesson) {
      return thunkAPI.rejectWithValue("Lesson not found in current list");
    }

    try {
      const res = await fetch(
        `${API_URL}/teacher/students/${studentId}/lessons/${lesson.id}/unlock`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = body.error || `Request failed with ${res.status}`;
        return thunkAPI.rejectWithValue(msg);
      }

      // We only need to mark it as unlocked locally
      return { lessonId: lesson.id, order: lesson.order };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || "Network error");
    }
  }
);

const teacherSlice = createSlice({
  name: "teacher",
  initialState: {
    students: [],
    loading: false,
    error: null,
    selectedStudentId: null,
    studentLessons: [],
    lessonsLoading: false,
    lessonsError: null,
  },
  reducers: {
    setSelectedStudent(state, action) {
      state.selectedStudentId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // students list
      .addCase(fetchTeacherStudents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeacherStudents.fulfilled, (state, action) => {
        state.loading = false;
        state.students = action.payload;
        // default to first student if none selected yet
        if (!state.selectedStudentId && action.payload.length > 0) {
          state.selectedStudentId = action.payload[0].id;
        }
      })
      .addCase(fetchTeacherStudents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load students";
      })

      // student lessons
      .addCase(fetchTeacherStudentLessons.pending, (state) => {
        state.lessonsLoading = true;
        state.lessonsError = null;
      })
      .addCase(fetchTeacherStudentLessons.fulfilled, (state, action) => {
        state.lessonsLoading = false;
        state.studentLessons = action.payload.lessons || [];
      })
      .addCase(fetchTeacherStudentLessons.rejected, (state, action) => {
        state.lessonsLoading = false;
        state.lessonsError = action.payload || "Failed to load student lessons";
      })

      // unlock lesson
      .addCase(unlockStudentLesson.fulfilled, (state, action) => {
        const { lessonId } = action.payload;
        state.studentLessons = state.studentLessons.map((l) =>
          l.id === lessonId ? { ...l, isUnlocked: true } : l
        );
      })
      .addCase(unlockStudentLesson.rejected, (state, action) => {
        state.lessonsError = action.payload || "Failed to unlock lesson";
      });
  },
});

export const { setSelectedStudent } = teacherSlice.actions;
export default teacherSlice.reducer;
